import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSignOutAlt, FaUserEdit, FaCheck, FaTimes, FaCompass, FaFingerprint, FaIdCard } from 'react-icons/fa';
import axios from 'axios';
import { BASE_URL } from '../Utils/Function';
import BackgroundEffect from './BackgroundEffect';

const Profile = () => {
    const [user, setUser] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({ user_name: '', mobile_no: '' });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            const userData = JSON.parse(storedUser);
            setUser(userData);
            setFormData({
                user_name: userData.user_name || '',
                mobile_no: userData.mobile_no || ''
            });
        } else {
            navigate('/login');
        }
    }, [navigate]);

    const handleLogout = () => {
        localStorage.removeItem('user');
        navigate('/login');
    };

    const handleUpdate = async () => {
        setIsLoading(true);
        setError('');
        setSuccessMsg('');

        try {
            const response = await axios.put(`${BASE_URL}/users/updateUser.php`, {
                user_id: user.user_id,
                user_name: formData.user_name,
                mobile_no: formData.mobile_no
            });

            if (response.data.status === 200 || response.data.status === 201) {
                const updatedUser = { ...user, ...formData };
                setUser(updatedUser);
                localStorage.setItem('user', JSON.stringify(updatedUser));
                setSuccessMsg('Structure Successfully Modified');
                setTimeout(() => {
                    setIsEditing(false);
                    setSuccessMsg('');
                }, 1500);
            } else {
                setError(response.data.message || 'Restructuring Failed');
            }
        } catch (err) {
            setError('System Error: ' + (err.response?.data?.message || 'Connection Interrupted'));
        } finally {
            setIsLoading(false);
        }
    };

    if (!user) return null;

    return (
        <div className="profile-immersive-page">
            <BackgroundEffect />

            <div className="profile-blueprint-container container">
                {/* Hero / Header Section */}
                <div className="profile-hero pt-5 mt-5 mb-5">
                    <div className="row align-items-end">
                        <div className="col-lg-8">
                            <span className="section-index mb-2">ARCHITECTURAL IDENTITY</span>
                            <h1 className="profile-title display-2 fw-lighter m-0">
                                {isEditing ? 'RECONFIGURING' : (user.user_name || 'USER SESSION')}
                            </h1>
                        </div>
                        <div className="col-lg-4 text-lg-end mt-4 mt-lg-0">
                            {!isEditing ? (
                                <button className="btn-arch-action" onClick={() => setIsEditing(true)}>
                                    <FaUserEdit className="me-2" /> EDIT PROFILE
                                </button>
                            ) : (
                                <div className="d-flex gap-2 justify-content-lg-end">
                                    <button className="btn-arch-action success" onClick={handleUpdate} disabled={isLoading}>
                                        {isLoading ? '...' : <><FaCheck /> COMMIT</>}
                                    </button>
                                    <button className="btn-arch-action cancel" onClick={() => setIsEditing(false)} disabled={isLoading}>
                                        <FaTimes />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="blueprint-line mt-4"></div>
                </div>

                {/* Notifications */}
                <div className="row justify-content-center">
                    <div className="col-lg-12">
                        {error && <div className="alert-arch error">{error}</div>}
                        {successMsg && <div className="alert-arch success">{successMsg}</div>}
                    </div>
                </div>

                {/* Main Content Sections */}
                <div className="row g-4">
                    {/* Identity Section */}
                    <div className="col-lg-8">
                        <div className="profile-section-block">
                            <h3 className="section-index mb-5">STRUCTURAL PARAMETERS</h3>

                            <div className="premium-data-row mb-5">
                                <div className="row align-items-center">
                                    <div className="col-md-4">
                                        <div className="data-header-box">
                                            <FaIdCard className="mb-2" />
                                            <span>FULL LEGAL NAME</span>
                                        </div>
                                    </div>
                                    <div className="col-md-8">
                                        {isEditing ? (
                                            <input
                                                type="text"
                                                className="arch-blueprint-input active"
                                                value={formData.user_name}
                                                onChange={(e) => setFormData({ ...formData, user_name: e.target.value })}
                                                placeholder="Enter Full Name"
                                            />
                                        ) : (
                                            <div className="data-value-box">{user.user_name || 'NOT DEFINED'}</div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="premium-data-row mb-5">
                                <div className="row align-items-center">
                                    <div className="col-md-4">
                                        <div className="data-header-box">
                                            <FaFingerprint className="mb-2" />
                                            <span>MOBILE LINK</span>
                                        </div>
                                    </div>
                                    <div className="col-md-8">
                                        {isEditing ? (
                                            <input
                                                type="text"
                                                className="arch-blueprint-input active"
                                                value={formData.mobile_no}
                                                onChange={(e) => setFormData({ ...formData, mobile_no: e.target.value })}
                                                placeholder="XXXXXXXXXX"
                                            />
                                        ) : (
                                            <div className="data-value-box">{user.mobile_no || 'NOT DEFINED'}</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Actions Section */}
                    <div className="col-lg-4">
                        <div className="profile-section-block alt-scheme">
                            <h3 className="section-index mb-5">ACCOUNT ACTIONS</h3>

                            <div className="mt-2">
                                <button className="btn-logout-minimal w-100" onClick={() => setShowLogoutModal(true)}>
                                    <FaSignOutAlt className="me-2" /> LOGOUT
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bottom Architectural Element */}
                <div className="profile-footer-mark text-center mt-5 pt-5 opacity-25">
                    <FaCompass size={40} className="mb-3" />
                    <p className="small letter-spacing-5">AL-WASEEQ DESIGN • VERTICAL STRUCTURES</p>
                </div>
            </div>

            {/* Logout Confirmation Modal */}
            {showLogoutModal && (
                <div className="logout-modal-overlay">
                    <div className="logout-modal-content">
                        <div className="text-center mb-4">
                            <div className="logout-icon-circle mb-3">
                                <FaSignOutAlt size={24} color="var(--secondary-color)" />
                            </div>
                            <h3 className="section-index" style={{ fontSize: '0.9rem' }}>TERMINATE SESSION?</h3>
                            <p className="small text-white-50 mt-2">Are you sure you want to end your current architectural session?</p>
                        </div>
                        <div className="d-grid gap-2">
                            <button className="btn-arch-action success w-100" onClick={handleLogout}>
                                <FaCheck className="me-2" /> YES, LOGOUT
                            </button>
                            <button className="btn-arch-action cancel w-100" onClick={() => setShowLogoutModal(false)}>
                                <FaTimes className="me-2" /> STAY ONLINE
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .profile-immersive-page {
                    min-height: 100vh;
                    padding-bottom: 50px;
                    color: #fff;
                    position: relative;
                }
                
                .profile-title {
                    letter-spacing: -2px;
                    color: var(--secondary-color);
                    text-transform: uppercase;
                }
                
                .blueprint-line {
                    height: 1px;
                    background: linear-gradient(90deg, var(--secondary-color), transparent);
                    width: 100%;
                }
                
                /* Premium Data Rows */
                .premium-data-row {
                    position: relative;
                }
                
                .data-header-box {
                    display: flex;
                    flex-direction: column;
                    border-left: 1px solid var(--secondary-color);
                    padding-left: 15px;
                    margin-bottom: 10px;
                }
                
                .data-header-box span {
                    font-size: 0.6rem;
                    color: var(--secondary-color);
                    letter-spacing: 3px;
                }
                
                .data-header-box svg {
                    color: var(--secondary-color);
                    font-size: 1rem;
                    opacity: 0.8;
                }
                
                .data-value-box {
                    font-size: 1.6rem;
                    font-weight: 200;
                    letter-spacing: 1px;
                    padding: 15px 0;
                    color: #fff;
                    background: linear-gradient(90deg, rgba(255,255,255,0.03), transparent);
                    padding-left: 20px;
                }
                
                .arch-blueprint-input {
                    background: rgba(255,255,255,0.05);
                    border: 1px solid rgba(197, 160, 89, 0.2);
                    color: var(--secondary-color);
                    font-size: 1.4rem;
                    width: 100%;
                    outline: none;
                    font-weight: 300;
                    padding: 15px 20px;
                    transition: all 0.3s ease;
                }
                
                .arch-blueprint-input.active {
                    border-color: var(--secondary-color);
                    box-shadow: 0 0 20px rgba(197, 160, 89, 0.1);
                }
                
                .btn-arch-action {
                    background: transparent;
                    color: var(--secondary-color);
                    border: 1px solid var(--secondary-color);
                    padding: 12px 20px;
                    font-size: 0.7rem;
                    letter-spacing: 2px;
                    text-transform: uppercase;
                    transition: all 0.3s ease;
                }
                
                .btn-arch-action:hover {
                    background: var(--secondary-color);
                    color: #000;
                }
                
                .btn-arch-action.success { border-color: #28a745; color: #28a745; }
                .btn-arch-action.success:hover { background: #28a745; color: #fff; }
                
                .btn-arch-action.cancel { border-color: rgba(255,255,255,0.2); color: rgba(255,255,255,0.5); }
                .btn-arch-action.cancel:hover { background: #dc3545; color: #fff; border-color: #dc3545; }
                
                @keyframes pulseStatus {
                    0% { opacity: 0.5; }
                    50% { opacity: 1; }
                    100% { opacity: 0.5; }
                }
                
                .btn-logout-minimal {
                    background: transparent;
                    border: 1px solid rgba(220, 53, 69, 0.2);
                    color: #ff4d4d;
                    padding: 15px;
                    font-size: 0.65rem;
                    letter-spacing: 3px;
                    transition: all 0.3s ease;
                }
                
                .btn-logout-minimal:hover {
                    background: #dc3545;
                    color: #fff;
                    border-color: #dc3545;
                }
                
                .alert-arch {
                    padding: 15px;
                    border: 1px solid;
                    margin-bottom: 30px;
                    text-align: center;
                    font-size: 0.75rem;
                    letter-spacing: 1px;
                }
                .alert-arch.error { border-color: #dc3545; color: #dc3545; background: rgba(220, 53, 69, 0.05); }
                .alert-arch.success { border-color: #28a745; color: #28a745; background: rgba(40, 167, 69, 0.05); }
                
                /* Logout Modal Styles */
                .logout-modal-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(0, 0, 0, 0.85);
                    backdrop-filter: blur(10px);
                    z-index: 10000;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 20px;
                    animation: fadeIn 0.3s ease;
                }
                
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                
                .logout-modal-content {
                    background: #0A0A0A;
                    border: 1px solid var(--glass-border);
                    padding: 40px 30px;
                    max-width: 400px;
                    width: 100%;
                    box-shadow: 0 40px 100px rgba(0,0,0,0.5);
                    position: relative;
                }
                
                .logout-icon-circle {
                    width: 60px;
                    height: 60px;
                    border: 1px solid var(--secondary-color);
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto;
                }
                
                @media (max-width: 768px) {
                    .profile-hero { padding-top: 30px !important; margin-top: 20px !important; }
                    .display-2 { font-size: clamp(1.8rem, 8vw, 2.5rem); }
                    .data-value-box { font-size: clamp(1rem, 4vw, 1.3rem); padding: 12px; }
                    .arch-blueprint-input { font-size: 1.1rem; padding: 12px; }
                    .data-header-box { border-left-width: 2px; margin-bottom: 5px; }
                    .profile-section-block { padding: 25px 20px !important; }
                }
                
                @media (max-width: 576px) {
                    .btn-arch-action { padding: 10px 15px; font-size: 0.6rem; width: 100%; justify-content: center; }
                    .profile-hero .text-lg-end { text-align: left !important; }
                }
            `}</style>
        </div>
    );
};

export default Profile;
